import { homeScheduleApi, homeWageApi, commonWageApi } from '@/services/api';
import {
  calcHomeWagePolicyCenterSavings,
  calcHomeWagePolicyEmployeeSavingsDetail,
  calcHomeWageCombinedLunch,
  parseHomeWageTaxRateValue,
  type HomeWagePolicyEmployeeSavingsDetail,
  type HomeWagePolicySavingsRateInput,
} from '@/policies/homeWageSettingPolicy';
import { buildEmpMatchKey } from '@/utils/empMatchKey';

export interface HomeWageNontaxSavingsMonthRow {
  month: number;
  yearMonth: string;
  hasData: boolean;
  employeeCount: number;
  saving: number;
  cumulative: number;
}

export interface HomeWageNontaxSavingsYearReport {
  year: number;
  monthCount: number;
  yearTotal: number;
  monthly: HomeWageNontaxSavingsMonthRow[];
}

export interface HomeWageNontaxSavingsReportResult {
  yearReport: HomeWageNontaxSavingsYearReport;
  cumulativeTotal: number;
}

const taxRateCache = new Map<number, HomeWagePolicySavingsRateInput>();
const sanRateCache = new Map<string, number>();

const resolveSanRateFromPayment = (data: Record<string, unknown> | null | undefined): number | null => {
  const raw = data?.cwp_san_rate ?? data?.sanRate ?? data?.hwpay_san_rate;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

async function loadTaxRates(year: number): Promise<HomeWagePolicySavingsRateInput> {
  const cached = taxRateCache.get(year);
  if (cached) return cached;

  const response = await commonWageApi.getTaxRate(year);
  const t = response.data?.data ?? response.data ?? {};
  const rates: HomeWagePolicySavingsRateInput = {
    rateYeon: parseHomeWageTaxRateValue(t.rateYeon ?? t.cbt_tr_yeon, 0.045),
    rateGun: parseHomeWageTaxRateValue(t.rateGun ?? t.cbt_tr_gun, 0.03545),
    rateJang: parseHomeWageTaxRateValue(t.rateJang ?? t.cbt_tr_jang, 0.1295),
    rateGo: parseHomeWageTaxRateValue(t.rateGo ?? t.cbt_tr_go, 0.009),
    rateSil: parseHomeWageTaxRateValue(t.rateSil ?? t.cbt_tr_sil, 0.0065),
    rateSan: parseHomeWageTaxRateValue(t.rateSan ?? t.cbt_tr_san, 0.007),
  };
  taxRateCache.set(year, rates);
  return rates;
}

async function loadSanRate(
  mCode: string,
  yearMonth: string,
  bizGubun: string,
  fallback: number
): Promise<number> {
  const cacheKey = `${mCode}|${yearMonth}|${bizGubun}`;
  if (sanRateCache.has(cacheKey)) return sanRateCache.get(cacheKey)!;

  try {
    const response = await homeWageApi.getPaymentDate({ mCode, yearMonth, bizGubun });
    const fromPayment = resolveSanRateFromPayment(response.data?.data ?? response.data);
    const rate = fromPayment ?? fallback;
    sanRateCache.set(cacheKey, rate);
    return rate;
  } catch {
    sanRateCache.set(cacheKey, fallback);
    return fallback;
  }
}

function normalizeWageRow(row: Record<string, unknown>) {
  return {
    job: (row.job ?? row.cwhd_job) as string | null,
    birth: row.birth ?? row.cwhd_birth,
    hour: row.hour ?? row.cwhd_hour,
    lunch: calcHomeWageCombinedLunch(
      Number(row.lunch ?? row.cwhd_lunch) || 0,
      Number(row.lunchDirector ?? row.cwhd_lunch_director) || 0
    ),
    overtime: Number(row.overtime ?? row.cwhd_overtime) || 0,
    night: Number(row.night ?? row.cwhd_night) || 0,
    holiday: Number(row.holiday ?? row.cwhd_holiday) || 0,
    overtimeNontax: Number(row.overtimeNontax ?? row.cwhd_overtime_nontax) || 0,
    nightNontax: Number(row.nightNontax ?? row.cwhd_night_nontax) || 0,
    holidayNontax: Number(row.holidayNontax ?? row.cwhd_holiday_nontax) || 0,
    productionNontax: (row.productionNontax ?? row.cwhd_production_nontax) as string | null,
    name: String(row.name ?? row.cwhd_name ?? '').trim(),
    birthKey: row.birth ?? row.cwhd_birth,
  };
}

export type { HomeWagePolicyEmployeeSavingsDetail };

export interface HomeWageEmployeeSavingsDetailRow {
  name: string;
  job: string;
  nontaxLabel: string;
  nontaxAmount: number;
  savingAmount: number;
  isFirstLine: boolean;
  lineCount: number;
  employeeTotalSaving: number;
}

function sumMonthSaving(
  rows: Record<string, unknown>[],
  year: number,
  month: number,
  rates: HomeWagePolicySavingsRateInput,
  joinDateByKey: Map<string, string | null>
): number {
  let total = 0;
  for (const raw of rows) {
    const row = normalizeWageRow(raw);
    if (!row.name) continue;
    const key = buildEmpMatchKey(row.name, row.birthKey as string | null);
    total += calcHomeWagePolicyCenterSavings(
      row,
      year,
      month,
      rates,
      joinDateByKey.get(key) ?? null
    );
  }
  return total;
}

async function fetchMonthRows(mCode: string, yearMonth: string, bizGubun: string) {
  const response = await homeScheduleApi.getDirectorWageData(mCode, yearMonth, bizGubun);
  if (!response.data?.success || !Array.isArray(response.data.data)) return [];
  return response.data.data as Record<string, unknown>[];
}

async function fetchYearMonthRows(
  mCode: string,
  year: number,
  bizGubun: string
): Promise<Map<number, Record<string, unknown>[]>> {
  const tasks = Array.from({ length: 12 }, (_, idx) => {
    const month = idx + 1;
    const yearMonth = `${year}${String(month).padStart(2, '0')}`;
    return fetchMonthRows(mCode, yearMonth, bizGubun).then((rows) => ({ month, rows }));
  });
  const results = await Promise.all(tasks);
  const map = new Map<number, Record<string, unknown>[]>();
  for (const { month, rows } of results) {
    map.set(month, rows);
  }
  return map;
}

async function calcMonthSavingFromRows(
  rows: Record<string, unknown>[],
  year: number,
  month: number,
  mCode: string,
  bizGubun: string,
  baseRates: HomeWagePolicySavingsRateInput,
  joinDateByKey: Map<string, string | null>
): Promise<number> {
  if (!rows.length) return 0;
  const yearMonth = `${year}${String(month).padStart(2, '0')}`;
  const sanRate = await loadSanRate(mCode, yearMonth, bizGubun, baseRates.rateSan);
  return sumMonthSaving(rows, year, month, { ...baseRates, rateSan: sanRate }, joinDateByKey);
}

export async function fetchHomeWageMonthSaving(params: {
  mCode: string;
  bizGubun: string;
  year: number;
  month: number;
  joinDateByKey?: Map<string, string | null>;
}): Promise<{ saving: number; employeeCount: number; hasData: boolean }> {
  const { mCode, bizGubun, year, month } = params;
  const yearMonth = `${year}${String(month).padStart(2, '0')}`;
  const rows = await fetchMonthRows(mCode, yearMonth, bizGubun);
  if (!rows.length) {
    return { saving: 0, employeeCount: 0, hasData: false };
  }

  const baseRates = await loadTaxRates(year);
  const sanRate = await loadSanRate(mCode, yearMonth, bizGubun, baseRates.rateSan);
  const rates = { ...baseRates, rateSan: sanRate };
  const saving = sumMonthSaving(rows, year, month, rates, params.joinDateByKey ?? new Map());

  return { saving, employeeCount: rows.length, hasData: true };
}

/** 관리자 — 해당 월 사원별 비과세·절감 상세 */
export async function fetchHomeWageEmployeeSavingsDetail(params: {
  mCode: string;
  bizGubun: string;
  year: number;
  month: number;
  joinDateByKey?: Map<string, string | null>;
}): Promise<HomeWagePolicyEmployeeSavingsDetail[]> {
  const { mCode, bizGubun, year, month } = params;
  const yearMonth = `${year}${String(month).padStart(2, '0')}`;
  const rows = await fetchMonthRows(mCode, yearMonth, bizGubun);
  if (!rows.length) return [];

  const baseRates = await loadTaxRates(year);
  const sanRate = await loadSanRate(mCode, yearMonth, bizGubun, baseRates.rateSan);
  const rates = { ...baseRates, rateSan: sanRate };
  const joinDateByKey = params.joinDateByKey ?? new Map();

  const details: HomeWagePolicyEmployeeSavingsDetail[] = [];
  for (const raw of rows) {
    const row = normalizeWageRow(raw);
    if (!row.name) continue;
    const key = buildEmpMatchKey(row.name, row.birthKey as string | null);
    const detail = calcHomeWagePolicyEmployeeSavingsDetail(
      row,
      year,
      month,
      rates,
      joinDateByKey.get(key) ?? null
    );
    if (detail.totalSaving > 0) {
      details.push(detail);
    }
  }

  return details.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
}

export function flattenEmployeeSavingsDetails(
  details: HomeWagePolicyEmployeeSavingsDetail[]
): HomeWageEmployeeSavingsDetailRow[] {
  const flat: HomeWageEmployeeSavingsDetailRow[] = [];
  for (const emp of details) {
    const lineCount = emp.lines.length;
    emp.lines.forEach((line, idx) => {
      flat.push({
        name: emp.name,
        job: emp.job,
        nontaxLabel: line.nontaxLabel,
        nontaxAmount: line.nontaxAmount,
        savingAmount: line.savingAmount,
        isFirstLine: idx === 0,
        lineCount,
        employeeTotalSaving: emp.totalSaving,
      });
    });
  }
  return flat;
}

export async function fetchHomeWageNontaxSavingsReport(params: {
  mCode: string;
  bizGubun: string;
  year: number;
  joinDateByKey?: Map<string, string | null>;
  cumulativeFromYear?: number;
}): Promise<HomeWageNontaxSavingsReportResult> {
  const { mCode, bizGubun, year, joinDateByKey = new Map() } = params;
  const cumulativeFromYear = params.cumulativeFromYear ?? 2020;

  const monthly: HomeWageNontaxSavingsMonthRow[] = [];
  let yearTotal = 0;
  let monthCount = 0;

  const baseRates = await loadTaxRates(year);
  const yearRowsMap = await fetchYearMonthRows(mCode, year, bizGubun);

  for (let month = 1; month <= 12; month += 1) {
    const yearMonth = `${year}${String(month).padStart(2, '0')}`;
    const rows = yearRowsMap.get(month) ?? [];
    if (!rows.length) {
      monthly.push({
        month,
        yearMonth,
        hasData: false,
        employeeCount: 0,
        saving: 0,
        cumulative: 0,
      });
      continue;
    }

    const saving = await calcMonthSavingFromRows(
      rows,
      year,
      month,
      mCode,
      bizGubun,
      baseRates,
      joinDateByKey
    );
    yearTotal += saving;
    monthCount += 1;
    monthly.push({
      month,
      yearMonth,
      hasData: true,
      employeeCount: rows.length,
      saving,
      cumulative: 0,
    });
  }

  let cumulativeTotal = 0;
  for (let y = cumulativeFromYear; y <= year; y += 1) {
    const yRates = await loadTaxRates(y);
    const rowsMap = y === year ? yearRowsMap : await fetchYearMonthRows(mCode, y, bizGubun);
    for (let month = 1; month <= 12; month += 1) {
      const rows = rowsMap.get(month) ?? [];
      if (!rows.length) continue;
      cumulativeTotal += await calcMonthSavingFromRows(
        rows,
        y,
        month,
        mCode,
        bizGubun,
        yRates,
        joinDateByKey
      );
    }
  }

  let runningInYear = 0;
  for (const row of monthly) {
    if (row.hasData) runningInYear += row.saving;
    row.cumulative = runningInYear;
  }

  return {
    yearReport: { year, monthCount, yearTotal, monthly },
    cumulativeTotal,
  };
}

export function buildJoinDateMapFromEmployees(
  employees: Array<{ ce_name?: string; emp_name?: string; name?: string; ce_birth?: string; emp_birth?: string; birth?: string; ce_join_date?: string; clci_work_start_date?: string }>
): Map<string, string | null> {
  const map = new Map<string, string | null>();
  for (const emp of employees) {
    const name = String(emp.ce_name ?? emp.emp_name ?? emp.name ?? '').trim();
    if (!name) continue;
    const birth = emp.ce_birth ?? emp.emp_birth ?? emp.birth ?? null;
    const join = emp.ce_join_date ?? emp.clci_work_start_date ?? null;
    map.set(buildEmpMatchKey(name, birth), join ? String(join).split('T')[0] : null);
  }
  return map;
}
